import { useState, useEffect, memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useScrollDebounce } from '../../../hooks/useScrollDebounce';

function Navbar() {
  const isScrolled = useScrollDebounce(50, 100);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const toggleUserMenu = useCallback(() => {
    setShowUserMenu(prev => !prev);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showUserMenu && !(e.target as Element).closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 min-w-[44px] min-h-[44px]">
            <div className="w-10 h-10 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/5645efc0cd1a8c3e708dcbbf89a13d5d/191562cd99da914f73f54f2bcc990a53.png" 
                alt="Agent IA Pro Logo" 
                className="w-full h-full object-contain"
                width="40"
                height="40"
                loading="eager"
              />
            </div>
            <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              Agent IA Pro
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Accueil
            </Link>
            <Link
              to="/case-studies"
              className={`min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Cas Clients
            </Link>
            <Link
              to="/blog"
              className={`min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className={`min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Contact
            </Link>

            {isAuthenticated && user ? (
              <div className="relative user-menu-container">
                <button
                  onClick={toggleUserMenu}
                  className={`min-w-[44px] min-h-[44px] flex items-center gap-2 px-4 py-2 rounded-lg transition-all cursor-pointer ${
                    isScrolled ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                  }`}
                >
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                  </div>
                  <span className={`text-sm font-medium ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
                    {user.firstName}
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform ${showUserMenu ? 'rotate-180' : ''} ${isScrolled ? 'text-slate-600' : 'text-white/80'}`}></i>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-200">
                      <p className="text-sm font-medium text-slate-900">{user.firstName} {user.lastName}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      className="min-h-[44px] flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="ri-dashboard-line"></i>
                      Mon Dashboard
                    </Link>
                    <Link
                      to="/dashboard?tab=settings"
                      className="min-h-[44px] flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="ri-settings-3-line"></i>
                      Paramètres
                    </Link>
                    <div className="border-t border-slate-200 my-2"></div>
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className="min-h-[44px] w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-all cursor-pointer"
                    >
                      <i className="ri-logout-box-line"></i>
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-sm font-medium transition-colors whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                  }`}
                >
                  Connexion
                </Link>
                <Link
                  to="/signup"
                  className="min-w-[44px] min-h-[44px] px-6 py-2 bg-teal-600 text-white rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                >
                  Essai Gratuit
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button - Touch-friendly */}
          <button
            onClick={toggleMobileMenu}
            className={`md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
            aria-label="Menu mobile"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-gray-200">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                className={`min-h-[44px] inline-flex items-center text-sm font-medium ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
                onClick={closeMobileMenu}
              >
                Accueil
              </Link>
              <Link
                to="/case-studies"
                className={`min-h-[44px] inline-flex items-center text-sm font-medium ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
                onClick={closeMobileMenu}
              >
                Cas Clients
              </Link>
              <Link
                to="/blog"
                className={`min-h-[44px] inline-flex items-center text-sm font-medium ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
                onClick={closeMobileMenu}
              >
                Blog
              </Link>
              <Link
                to="/contact"
                className={`min-h-[44px] inline-flex items-center text-sm font-medium ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
                onClick={closeMobileMenu}
              >
                Contact
              </Link>

              {isAuthenticated && user ? (
                <>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-medium">
                        {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                          {user.firstName} {user.lastName}
                        </p>
                        <p className={`text-xs ${isScrolled ? 'text-gray-500' : 'text-white/70'}`}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/dashboard"
                    className={`min-h-[44px] inline-flex items-center text-sm font-medium ${
                      isScrolled ? 'text-gray-700' : 'text-white'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    Mon Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className={`min-h-[44px] inline-flex items-center text-sm font-medium text-left cursor-pointer ${
                      isScrolled ? 'text-red-600' : 'text-red-400'
                    }`}
                  >
                    Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`min-h-[44px] inline-flex items-center text-sm font-medium ${
                      isScrolled ? 'text-gray-700' : 'text-white'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/signup"
                    className="min-h-[44px] px-6 py-2 bg-teal-600 text-white rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                    onClick={closeMobileMenu}
                  >
                    Essai Gratuit
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default memo(Navbar);